/**
 * useInbox — hook para el inbox de notificaciones del admin (bell + drawer).
 *
 * Responsabilidades:
 *  - Cargar la lista al montar
 *  - Polling cada 15s para detectar nuevas (safety net)
 *  - Escuchar el event 'deenex:inbox:refresh' que cualquier componente puede
 *    disparar via `dispatchInboxRefresh()` tras una acción que sabe que
 *    generó una notif nueva (evita esperar el polling — bell actualiza al toque)
 *  - Exponer unreadCount para el badge
 *  - Acciones: markRead, markAllRead, dismiss, clearAll
 *  - refetch manual (cuando se abre el drawer)
 *
 * `enabled = false` no carga, no hace polling ni se suscribe: el inbox es de la plataforma
 * anterior y su endpoint no existe en el backend de Activa (cada vuelta sería un 404).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
    inboxApi,
    type InboxNotification,
} from "../services/api/inbox.api";
import { subscribeToInboxRefresh } from "../services/inbox-events";

const POLL_INTERVAL_MS = 15_000;

export function useInbox(enabled = true) {
    const [items, setItems] = useState<InboxNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(enabled);
    const mountedRef = useRef(true);

    const refetch = useCallback(async () => {
        const r = await inboxApi.list();
        if (!mountedRef.current) return;
        if (r.success && r.data) {
            setItems(r.data.items);
            setUnreadCount(r.data.unreadCount);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        if (!enabled) {
            setLoading(false);
            return () => {
                mountedRef.current = false;
            };
        }
        refetch();
        const interval = setInterval(refetch, POLL_INTERVAL_MS);
        // Pub/sub: cualquier acción puede pedir refresh con dispatchInboxRefresh()
        const unsubscribe = subscribeToInboxRefresh(() => refetch());
        return () => {
            mountedRef.current = false;
            clearInterval(interval);
            unsubscribe();
        };
    }, [refetch, enabled]);

    const markRead = useCallback(
        async (id: string) => {
            // Optimistic update
            setItems((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
            );
            setUnreadCount((c) => Math.max(0, c - 1));
            const r = await inboxApi.markRead(id);
            if (!r.success) refetch(); // rollback via refetch
        },
        [refetch],
    );

    const markAllRead = useCallback(async () => {
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
        const r = await inboxApi.markAllRead();
        if (!r.success) refetch();
    }, [refetch]);

    const dismiss = useCallback(
        async (id: string) => {
            const wasUnread = items.find((n) => n.id === id)?.read === false;
            setItems((prev) => prev.filter((n) => n.id !== id));
            if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
            const r = await inboxApi.dismiss(id);
            if (!r.success) refetch();
        },
        [items, refetch],
    );

    const clearAll = useCallback(async () => {
        setItems([]);
        setUnreadCount(0);
        const r = await inboxApi.clearAll();
        if (!r.success) refetch();
    }, [refetch]);

    return {
        items,
        unreadCount,
        loading,
        refetch,
        markRead,
        markAllRead,
        dismiss,
        clearAll,
    };
}
